import {
    formatNumber,
    getBudgetInputs,
    getTankSpecificationsResults,
    getTankResults,
    getTowerSpecificationsResults,
    getWarmUpCalculations,
    getCylindricalShellSection1Results,
    getCylindricalShellSection2Results,
    getCylindricalShellSection3Results,
    getDishedHeadSection1Results,
    getDishedHeadSection2Results,
    getDishedHeadSection3Results,
} from "../helpers/Helpers";

/**
 * Serializes the calculation results (the same data the UI renders through
 * ExcelResultTable / Helpers) into a generic PDF document payload:
 *
 *   PdfDocumentRequest { cover, sections[] }
 *     PdfSection    { title, subSections[] }
 *     PdfSubSection { title, blocks[] }
 *     PdfBlock      { type: "TABLE" | "TEXT", title, columns[], rows[], lines[] }
 *     PdfRow        { cells[], style }
 *
 * The backend is a dumb renderer: all labels, i18n and number formatting are
 * done here so the PDF stays 1:1 with the UI.
 */

// Mirrors ExcelResultTable.displayValue / PipeDimension.displayCellValue.
const fmtCell = (value) => {
    if (value == null || value === "") return "—";
    if (/^-?\d+\.?\d*$/.test(String(value).trim())) return formatNumber(value);
    return String(value);
};

const isSpacer = (row) =>
    (!row || (!row.label && (row.value == null || row.value === "") && !row.unit));

// Maps a Helpers row (label/value/unit + style flags) to a PdfRow.
const rowStyle = (row) => {
    if (row.error) return "ERROR";
    if (row.warning) return "WARNING";
    if (row.title) return "TITLE";
    if (row.highlight2 || row.ok) return "OK";
    if (row.highlight) return "HIGHLIGHT";
    if (row.emphasis) return "EMPHASIS";
    return "NORMAL";
};

const kvRows = (rows) =>
    (rows || []).map((row) => {
        if (isSpacer(row)) return { cells: ["", "", ""], style: "SPACER" };
        return {
            cells: [row.label ?? "", fmtCell(row.value), row.unit ?? ""],
            style: rowStyle(row),
        };
    });

// A key/value TABLE block from a Helpers row array.
const kvBlock = (title, rows) => ({
    type: "TABLE",
    title: title || null,
    columns: [],
    rows: kvRows(rows),
    lines: [],
});

// A multi-column grid TABLE block (pipe water/steam/nozzle tables).
const gridBlock = (title, columns, items, keyMap) => ({
    type: "TABLE",
    title: title || null,
    columns,
    rows: (items || []).map((item) => ({
        cells: keyMap.map((k) => fmtCell(item[k])),
        style: "NORMAL",
    })),
    lines: [],
});

const textBlock = (title, lines) => ({
    type: "TEXT",
    title: title || null,
    columns: [],
    rows: [],
    lines: (lines || []).filter((l) => l != null),
});

// Runs a block builder, swallowing errors from partial/missing datasets
// (some Helpers row-builders call .toFixed() without optional chaining).
const safe = (fn) => {
    try {
        return fn();
    } catch {
        return null;
    }
};

const subSection = (title, blocks) => {
    const clean = (blocks || []).filter(Boolean);
    return clean.length ? { title: title || null, blocks: clean } : null;
};

const section = (title, subSections) => {
    const clean = (subSections || []).filter(Boolean);
    return clean.length ? { title, subSections: clean } : null;
};

// ---- Section builders (mirror each calculation component) -------------------

const buildVesselSection = (vessel, t) => {
    if (!vessel) return null;
    const tank = subSection(t("budgetForm.vesselCalc.tank"), [
        safe(() => kvBlock(t("budgetForm.vesselCalc.inputData"), getBudgetInputs(vessel, t))),
        safe(() => kvBlock(t("budgetForm.vesselCalc.torisphericalDin"), getTankSpecificationsResults(vessel))),
        safe(() => kvBlock(t("budgetForm.vesselCalc.tankCapacity"), getTankResults(vessel, t))),
    ]);
    const tower = subSection(t("budgetForm.vesselCalc.tower"), [
        safe(() => kvBlock(t("budgetForm.vesselCalc.degassingTower"), getTowerSpecificationsResults(vessel))),
    ]);
    return section(t("budgetForm.steps.deaeratorCalculations"), [tank, tower]);
};

const buildPipeSection = (vessel, t) => {
    if (!vessel) return null;
    const p = vessel.pipeDimensionResult || {};

    const waterRows = [
        { label: "Alimentação Caldeiras", flow: `${p.degassingCapacity}`, speed: p.speedI21, interiorDiameter: p.interiorDiameter?.toFixed(0), specifiedDiameter: p.specifiedDiameter },
        { label: "Condensados", flow: p.condensateVolumetricFlow?.toFixed(1), speed: p.speedI26, interiorDiameter: p.condensateInteriorDiameter?.toFixed(0), specifiedDiameter: p.condensateSpecifiedDiameter },
        { label: "Água Make-up", flow: p.makeUpWaterVolumetricFlow?.toFixed(1), speed: p.speedI29, interiorDiameter: p.makeUpInteriorDiameter?.toFixed(0), specifiedDiameter: p.makeUpSpecifiedDiameter },
        { label: "Retorno Bombas", flow: p.pumpReturnFlowRate?.toFixed(1), speed: p.speedI32, interiorDiameter: p.pumpReturnInteriorDiameter?.toFixed(0), specifiedDiameter: p.pumpReturnSpecifiedDiameter },
    ];
    const steamRows = [
        { label: "Vapor Desgasificação", flow: p.specificThermalEnergyQ22?.toFixed(1), speed: p.speedR22, pressure: p.availableSteamPressureS22?.toFixed(2), interiorDiameter: p.vapourInteriorDiameter?.toFixed(0), specifiedDiameter: p.vapourSpecifiedDiameter },
        { label: "Vapor Aquecimento", flow: p.steamMassFlowRateQ24?.toFixed(1), speed: p.speedR24, pressure: p.vapourHeatingPressure?.toFixed(2), interiorDiameter: p.vapourHeatingInteriorDiameter?.toFixed(0), specifiedDiameter: p.vapourHeatingSpecifiedDiameter },
    ];

    const pipes = subSection(t("budgetForm.pipeDimension.tabPipes"), [
        safe(() => gridBlock("Tubagens de Água",
            ["Designação", "Caudal [m³/h]", "Vel. [m/s]", "Ø Int. [mm]", "Ø Adotado DN"],
            waterRows, ["label", "flow", "speed", "interiorDiameter", "specifiedDiameter"])),
        safe(() => gridBlock("Tubagens de Vapor",
            ["Designação", "Caudal [kg/h]", "Vel. [m/s]", "Pressão [bar]", "Ø Int. [mm]", "Ø Adotado DN"],
            steamRows, ["label", "flow", "speed", "pressure", "interiorDiameter", "specifiedDiameter"])),
    ]);

    const heating = subSection(t("budgetForm.pipeDimension.tabHeating"), [
        safe(() => kvBlock(t("budgetForm.pipeDimension.tabHeating"), getWarmUpCalculations(vessel))),
    ]);

    // Nozzle list and vacuum breaker are intentionally excluded from the PDF.
    return section(t("budgetForm.steps.heatingPipeDiameter"), [pipes, heating]);
};

const buildCylindricalSection = (cylindrical, t) => {
    if (!cylindrical) return null;
    const rings = cylindrical.tankCylindricalShellSheetResult?.cylindricalShellSection1?.stiffeningRings;

    const shellSub = (isTower) => subSection(
        isTower ? t("budgetForm.vesselCalc.tower") : t("budgetForm.vesselCalc.tank"),
        [
            safe(() => kvBlock(t("budgetForm.cylindricalShellCalc.bodyTitle"), getCylindricalShellSection1Results(cylindrical, isTower, t))),
            safe(() => kvBlock(t("budgetForm.cylindricalShellCalc.largestNozzleTitle"), getCylindricalShellSection2Results(cylindrical, isTower, t))),
            safe(() => kvBlock(t("budgetForm.cylindricalShellCalc.openingCheckTitle"), getCylindricalShellSection3Results(cylindrical, isTower, t))),
            !isTower && rings
                ? textBlock(t("budgetForm.cylindricalShellCalc.stiffeningRingsTitle"), [
                    t("budgetForm.cylindricalShellCalc.stiffeningRingsLine1", {
                        diameter: typeof rings.diameterInMM === "number" ? rings.diameterInMM.toFixed(2) : "-",
                        lmax: rings.lmax ?? "-",
                    }),
                    t("budgetForm.cylindricalShellCalc.stiffeningRingsLine2", {
                        bodyLength: rings.bodyLength ?? "-",
                        rings: rings.numberOfStiffeningRings ?? "-",
                    }),
                    t("budgetForm.cylindricalShellCalc.stiffeningRingsLine3", {
                        rings: rings.numberOfStiffeningRings ?? "-",
                    }),
                ])
                : null,
        ]
    );

    return section(t("budgetForm.steps.cylindricalShell"), [shellSub(false), shellSub(true)]);
};

const buildDishedSection = (dishedHead, t) => {
    if (!dishedHead) return null;
    const dishedSub = (isTower) => subSection(
        isTower ? t("budgetForm.vesselCalc.tower") : t("budgetForm.vesselCalc.tank"),
        [
            safe(() => kvBlock(t("budgetForm.dishedHeadCalc.torisphericalHeadsTitle"), getDishedHeadSection1Results(dishedHead, isTower, t))),
            safe(() => kvBlock(t("budgetForm.dishedHeadCalc.largestNozzleTitle"), getDishedHeadSection2Results(dishedHead, isTower, t))),
            safe(() => kvBlock(t("budgetForm.dishedHeadCalc.openingCheckTitle"), getDishedHeadSection3Results(dishedHead, isTower, t))),
        ]
    );
    return section(t("budgetForm.steps.dishedHead"), [dishedSub(false), dishedSub(true)]);
};

// Mirrors BudgetDimensionsTable groups: group headers + label/value rows in one table.
const buildDimensionsSection = (data, t) => {
    if (!data) return null;
    const f = (k) => t(`budgetForm.dimensionsTable.fields.${k}`);
    const g = (k) => t(`budgetForm.dimensionsTable.groups.${k}`);
    const withUnit = (v, u) => (v ? `${v} ${u}` : null);

    const groups = [
        { title: g("projectInfo"), rows: [
            { label: f("propose"), value: data.propose },
            { label: f("projectCode"), value: data.projectCode },
            { label: f("degassingCapacity"), value: withUnit(data.degassingCapacity, "t/h") },
        ] },
        { title: g("tankDimensions"), rows: [
            { label: f("diameter"), value: withUnit(data.tankDiameter, "mm") },
            { label: f("tankBodyLength"), value: withUnit(data.tankBodyLength, "mm") },
            { label: f("outerDiameter"), value: withUnit(data.tankOuterDiameter, "mm") },
            { label: f("shellThickness"), value: withUnit(data.tankShellThickness, "mm") },
            { label: f("headThickness"), value: withUnit(data.tankHeadThickness, "mm") },
            { label: f("totalVolume"), value: withUnit(data.tankTotalVolume, "m³") },
            { label: f("usefulVolume"), value: withUnit(data.tankUsefulVolume, "m³") },
            { label: f("material"), value: data.tankMaterial },
        ] },
        { title: g("towerDimensions"), rows: [
            { label: f("diameter"), value: withUnit(data.towerDiameter, "mm") },
            { label: f("height"), value: withUnit(data.towerHeight, "mm") },
            { label: f("shellThickness"), value: withUnit(data.towerShellThickness, "mm") },
            { label: f("headThickness"), value: withUnit(data.towerHeadThickness, "mm") },
            { label: f("numberOfSprayers"), value: data.numberOfSprayers },
            { label: f("numberOfTrays"), value: data.numberOfTrays },
            { label: f("material"), value: data.towerMaterial },
        ] },
        { title: g("operatingConditions"), rows: [
            { label: f("operatingPressure"), value: withUnit(data.operatingPressure, "barg") },
            { label: f("operatingTemperature"), value: withUnit(data.operatingTemperature, "°C") },
            { label: f("availableSteamPressure"), value: withUnit(data.availableSteamPressure, "barg") },
            { label: f("availableSteamTemperature"), value: withUnit(data.availableSteamTemperature, "°C") },
        ] },
    ];

    // Vacuum breaker and nozzle list groups are intentionally excluded from the PDF.

    const tableRows = [];
    groups.forEach((group) => {
        tableRows.push({ cells: [group.title], style: "GROUP" });
        group.rows.forEach((r) => tableRows.push({ cells: [r.label ?? "", r.value ?? "—"], style: "NORMAL" }));
    });

    const block = { type: "TABLE", title: null, columns: [], rows: tableRows, lines: [] };
    return section(t("budgetForm.steps.budget"), [subSection(null, [block])]);
};

export const buildDimensionsPdfPayload = (results, t) => {
    const { vessel, cylindrical, dishedHead, dimensions } = results || {};

    const cover = {
        propose: dimensions?.propose ?? "",
        projectCode: dimensions?.projectCode ?? "",
        degassingCapacity: dimensions?.degassingCapacity != null ? `${dimensions.degassingCapacity} t/h` : "",
    };

    const sections = [
        buildVesselSection(vessel, t),
        buildPipeSection(vessel, t),
        buildCylindricalSection(cylindrical, t),
        buildDishedSection(dishedHead, t),
        buildDimensionsSection(dimensions, t),
    ].filter(Boolean);

    return { cover, sections };
};
